import * as React from "react";
import { AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { HoloShimmer } from "@/components/Holo";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";
import { DesignRenderGroup } from "@/services/bedrock/dtos/DesignRenderGroup";
import { MiniPager } from "./MiniPager";

export interface RenderPreviewProps {
  group: DesignRenderGroup | undefined;
  aspectRatio: AspectRatio;
  isInFlight: boolean;
  isFailed: boolean;
  slowAfterMs?: number;
  onRetry: () => void;
  /** Classes for the image and its placeholder (rounding, borders). */
  imageClassName?: string;
  /** The tallest the image may be (any CSS length); null lets it fill its container's width. */
  maxHeight?: string | null;
  /** "mini" is prev / n of m / next under the image; "overlay" is a feed-style count and arrows on it. */
  pager?: "mini" | "overlay";
  /** Control which image shows, for a parent that draws its own indicator. */
  index?: number;
  onIndexChange?: (index: number) => void;
}

const DEFAULT_SLOW_AFTER_MS = 45_000;

/** How many images the post has, counting ones still rendering. */
export function slideCount(group: DesignRenderGroup | undefined): number {
  return Math.max(group?.totalSlides ?? 0, group?.designRenders.length ?? 0, 1);
}

const ASPECT_CLASS: Record<AspectRatio, string> = {
  square_1_1: "aspect-square",
  portrait_4_5: "aspect-[4/5]",
  portrait_9_16: "aspect-[9/16]",
};

const RATIO: Record<AspectRatio, number> = {
  square_1_1: 1,
  portrait_4_5: 4 / 5,
  portrait_9_16: 9 / 16,
};

export const RenderPreview: React.FC<RenderPreviewProps> = ({
  group,
  aspectRatio,
  isInFlight,
  isFailed,
  slowAfterMs = DEFAULT_SLOW_AFTER_MS,
  onRetry,
  imageClassName = "rounded-xl border border-slate-200",
  maxHeight = "60vh",
  pager = "mini",
  index: controlledIndex,
  onIndexChange,
}) => {
  const [ownIndex, setOwnIndex] = React.useState(0);
  // Image URLs that have finished loading in the browser.
  const [loaded, setLoaded] = React.useState<Record<string, true>>({});
  const markLoaded = (uri: string) => setLoaded(current => (current[uri] ? current : { ...current, [uri]: true }));
  const index = controlledIndex ?? ownIndex;
  const setIndex = (next: number) => (onIndexChange ? onIndexChange(next) : setOwnIndex(next));
  const [slow, setSlow] = React.useState(false);

  const renders = React.useMemo(
    () => [...(group?.designRenders ?? [])].sort((a, b) => a.slideIndex - b.slideIndex),
    [group?.designRenders]
  );
  const total = slideCount(group);
  const done = group?.completedSlides ?? renders.filter(r => r.uri).length;
  const working = isInFlight && !isFailed;

  // Restart the slow timer whenever another image lands.
  React.useEffect(() => {
    setSlow(false);
    if (!working) return;
    const timer = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(timer);
  }, [working, done, slowAfterMs]);

  React.useEffect(() => {
    if (index >= total) setIndex(0);
  }, [total, index]);

  // Bounded by both the height and the width so 9:16 designs stay inside the viewport.
  const frameStyle: React.CSSProperties = maxHeight
    ? { maxWidth: `min(100%, calc(${maxHeight} * ${RATIO[aspectRatio]}))` }
    : {};

  if (isFailed) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="render-failed">
        <AlertCircle className="size-8 text-slate-400" />
        <p className="font-semibold text-slate-800">We couldn't build this post.</p>
        <Button onClick={onRetry}>Try again</Button>
      </div>
    );
  }

  const current = renders[index];
  const showImage = !working ? !!current?.uri : current?.status === "success" && !!current.uri;
  const overlay = pager === "overlay" && total > 1;
  const imageLoaded = showImage && !!current?.uri && !!loaded[current.uri];
  // A box of the post's shape holds the space, and the shimmer stays over it until the image has
  // actually loaded, so the preview never collapses to nothing between the two.
  const media = (
    <div className={cn("relative w-full overflow-hidden", ASPECT_CLASS[aspectRatio], imageClassName)}>
      {showImage && current?.uri && (
        <img
          key={current.uri}
          src={current.uri}
          alt={`Image ${index + 1}`}
          onLoad={() => markLoaded(current.uri!)}
          className={cn(
            "absolute inset-0 size-full object-cover transition-opacity duration-300",
            imageLoaded ? "opacity-100" : "opacity-0"
          )}
        />
      )}
      {!imageLoaded && (
        <HoloShimmer
          aspectRatio={aspectRatio}
          className="absolute inset-0 size-full rounded-none"
          label="Building your post…"
          sublabel={total > 1 ? `${Math.min(done, total)} of ${total} images ready` : undefined}
        />
      )}
    </div>
  );

  return (
    <div className={cn("flex flex-col", pager === "overlay" ? "gap-0" : "gap-3")} data-testid="render-preview">
      <div className="group/media relative mx-auto w-full" style={frameStyle}>
        {media}
        {overlay && (
          <>
            <span className="absolute top-2.5 right-2.5 rounded-full bg-slate-900/70 px-2 py-0.5 text-xs font-medium text-white tabular-nums">
              {index + 1}/{total}
            </span>
            {index > 0 && (
              <button
                type="button"
                aria-label="Previous"
                onClick={() => setIndex(index - 1)}
                className="absolute top-1/2 left-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow cursor-pointer"
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            {index < total - 1 && (
              <button
                type="button"
                aria-label="Next"
                onClick={() => setIndex(index + 1)}
                className="absolute top-1/2 right-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow cursor-pointer"
              >
                <ChevronRight className="size-4" />
              </button>
            )}
          </>
        )}
      </div>

      {pager === "mini" && total > 1 && (
        <div className="flex justify-center">
          <MiniPager index={index} total={total} onChange={setIndex} />
        </div>
      )}

      {working && slow && (
        <p className="px-3 pt-2 text-center text-sm text-slate-500">Taking a little longer than usual. Hang tight.</p>
      )}
    </div>
  );
};
